import { Button, Carousel as CarouselAnt, Modal } from 'antd';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { FileData } from '../../../store/types';
import { IconArrowLeft } from '../Icon';

import styles from './index.module.scss';

interface CarouselProps {
  files: FileData[];
  className?: string;
}

function Carousel({ files, className }: CarouselProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [validFiles, setValidFiles] = useState<FileData[]>([]);
  const [orientations, setOrientations] = useState<
    Record<string, 'vertical' | 'horizontal'>
  >({});

  useEffect(() => {
    let isMounted = true;

    const loadImages = async () => {
      const checks = await Promise.all(
        files.map(
          (file) =>
            new Promise<FileData | null>((resolve) => {
              const img = new Image();
              img.src = file.location;

              img.onload = () => {
                const orientation =
                  img.naturalHeight > img.naturalWidth
                    ? 'vertical'
                    : 'horizontal';
                if (isMounted) {
                  setOrientations((prev) => ({
                    ...prev,
                    [file.id]: orientation,
                  }));
                }
                resolve(file);
              };

              img.onerror = () => {
                resolve(null);
              };
            })
        )
      );

      if (isMounted) {
        const filtered = checks.filter(
          (file): file is FileData => file !== null
        );
        setValidFiles(filtered);
      }
    };

    loadImages();

    return () => {
      isMounted = false;
    };
  }, [files]);

  const openFullscreen = (src: string) => {
    setFullscreenImage(src);
    setIsFullscreen(true);
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
    setFullscreenImage(null);
  };

  if (validFiles.length === 0) return null;

  return (
    <>
      <CarouselAnt
        arrows={validFiles.length > 1}
        dots={validFiles.length > 1}
        infinite
        className={clsx(styles.carousel, className)}
        prevArrow={
          <Button>
            <IconArrowLeft />
          </Button>
        }
        nextArrow={
          <Button>
            <IconArrowLeft className={styles.iconRight} />
          </Button>
        }
      >
        {validFiles.map((file) => {
          const orientation = orientations[file.id];

          return (
            <div key={file.id} className={styles.slide}>
              <img
                src={file.location}
                alt={file.originalName}
                onClick={() => openFullscreen(file.location)}
                className={clsx(styles.img, styles.clickable, {
                  [styles.vertical]: orientation === 'vertical',
                  [styles.horizontal]: orientation === 'horizontal',
                })}
              />
            </div>
          );
        })}
      </CarouselAnt>

      <Modal
        open={isFullscreen}
        footer={null}
        onCancel={closeFullscreen}
        closable={false}
        centered
        width="100vw"
        style={{
          top: 0,
          padding: 0,
          margin: 0,
          overflow: 'hidden',
        }}
        styles={{
          mask: {
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
          },
        }}
        modalRender={() => (
          <div className={styles.fullscreenWrapper} onClick={closeFullscreen}>
            {fullscreenImage && (
              <img
                src={fullscreenImage}
                alt="Fullscreen"
                onClick={(e) => {
                  e.stopPropagation();
                  closeFullscreen();
                }}
                className={styles.fullscreenImage}
              />
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                closeFullscreen();
              }}
              className={styles.btnFullscreenClose}
              aria-label="Close"
              style={{ pointerEvents: 'auto' }}
            >
              ×
            </button>
          </div>
        )}
      />
    </>
  );
}

export default Carousel;
